/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Subscriptions Service - Organization Subscription Management
 * ============================================================================
 *
 * Service layer for managing organization module subscriptions.
 * Provides CRUD operations for subscription items and management actions.
 *
 * ============================================================================
 */

import { get, post, put, del } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';
import {
  SubscriptionSummary,
  ModuleSubscriptionItem,
  CreateSubscriptionRequest,
  AddModuleRequest,
  ChangeTierRequest,
} from '@/types/superadmin.types';
import type { FeatureAddonItem } from '@/types/addon.types';

/**
 * Build the base URL for an organization's subscription
 */
const getBaseUrl = (orgId: string) => `/admin/organizations/${orgId}/subscription`;

/**
 * Subscriptions Service
 *
 * SuperAdmin APIs for managing organization module subscriptions.
 */
export const subscriptionsService = {
  // ========================================================================
  // Subscription Management
  // ========================================================================

  /**
   * Get subscription summary for an organization.
   */
  async getSubscription(orgId: string): Promise<SubscriptionSummary> {
    const response = await get<ApiResponse<SubscriptionSummary>>(getBaseUrl(orgId));
    return response.data;
  },

  /**
   * Create a new subscription for an organization.
   */
  async createSubscription(orgId: string, data: CreateSubscriptionRequest): Promise<SubscriptionSummary> {
    const response = await post<ApiResponse<SubscriptionSummary>>(getBaseUrl(orgId), data);
    return response.data;
  },

  /**
   * Update an existing subscription for an organization.
   */
  async updateSubscription(orgId: string, data: CreateSubscriptionRequest): Promise<SubscriptionSummary> {
    const response = await put<ApiResponse<SubscriptionSummary>>(getBaseUrl(orgId), data);
    return response.data;
  },

  /**
   * Get subscription statistics for an organization.
   */
  async getSubscriptionStats(orgId: string): Promise<Record<string, unknown>> {
    const response = await get<ApiResponse<Record<string, unknown>>>(`${getBaseUrl(orgId)}/stats`);
    return response.data;
  },

  // ========================================================================
  // Module Management
  // ========================================================================

  /**
   * Add a module to an existing subscription.
   */
  async addModule(orgId: string, data: AddModuleRequest): Promise<ModuleSubscriptionItem> {
    const response = await post<ApiResponse<ModuleSubscriptionItem>>(
      `${getBaseUrl(orgId)}/modules`,
      data
    );
    return response.data;
  },

  /**
   * Preview the impact of changing a module's tier (dry-run).
   * Returns add-ons that would be cancelled by the downgrade.
   */
  async previewTierChangeImpact(
    orgId: string,
    moduleCode: string,
    newTierCode: string
  ): Promise<FeatureAddonItem[]> {
    const response = await get<ApiResponse<FeatureAddonItem[]>>(
      `${getBaseUrl(orgId)}/modules/${moduleCode}/tier-change-impact`,
      { newTierCode }
    );
    return response.data;
  },

  /**
   * Change the tier for a subscribed module.
   */
  async changeTier(
    orgId: string,
    moduleCode: string,
    data: ChangeTierRequest
  ): Promise<ModuleSubscriptionItem> {
    const response = await put<ApiResponse<ModuleSubscriptionItem>>(
      `${getBaseUrl(orgId)}/modules/${moduleCode}/tier`,
      data
    );
    return response.data;
  },

  /**
   * Update user count for a per-user module.
   */
  async updateUserCount(
    orgId: string,
    moduleCode: string,
    userCount: number
  ): Promise<ModuleSubscriptionItem> {
    const response = await put<ApiResponse<ModuleSubscriptionItem>>(
      `${getBaseUrl(orgId)}/modules/${moduleCode}/users?userCount=${userCount}`,
      {}
    );
    return response.data;
  },

  /**
   * Remove a module from the subscription.
   */
  async removeModule(orgId: string, moduleCode: string, reason?: string): Promise<void> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    await del<ApiResponse<void>>(`${getBaseUrl(orgId)}/modules/${moduleCode}${params}`);
  },

  // ========================================================================
  // Trial Management
  // ========================================================================

  /**
   * Convert a trial module to active subscription.
   */
  async convertTrial(orgId: string, moduleCode: string): Promise<ModuleSubscriptionItem> {
    const response = await post<ApiResponse<ModuleSubscriptionItem>>(
      `${getBaseUrl(orgId)}/modules/${moduleCode}/convert-trial`
    );
    return response.data;
  },

  // ========================================================================
  // Subscription Status Management
  // ========================================================================

  /**
   * Suspend (disable) a subscription.
   */
  async suspendSubscription(orgId: string, reason?: string): Promise<SubscriptionSummary> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await post<ApiResponse<SubscriptionSummary>>(
      `${getBaseUrl(orgId)}/suspend${params}`
    );
    return response.data;
  },

  /**
   * Reactivate a suspended subscription.
   */
  async reactivateSubscription(orgId: string): Promise<SubscriptionSummary> {
    const response = await post<ApiResponse<SubscriptionSummary>>(
      `${getBaseUrl(orgId)}/reactivate`
    );
    return response.data;
  },

  /**
   * Extend subscription period by specified days.
   */
  async extendSubscription(orgId: string, days: number): Promise<SubscriptionSummary> {
    const response = await post<ApiResponse<SubscriptionSummary>>(
      `${getBaseUrl(orgId)}/extend?days=${days}`
    );
    return response.data;
  },

  // ========================================================================
  // Feature Management
  // ========================================================================

  /**
   * Toggle a feature within a module license.
   */
  async toggleFeature(
    orgId: string,
    moduleCode: string,
    featureCode: string,
    enabled: boolean
  ): Promise<Record<string, boolean>> {
    const response = await put<ApiResponse<Record<string, boolean>>>(
      `${getBaseUrl(orgId)}/modules/${moduleCode}/features/${featureCode}`,
      { enabled }
    );
    return response.data;
  },

  /**
   * Get current feature states for a module license.
   */
  async getModuleFeatures(
    orgId: string,
    moduleCode: string
  ): Promise<Record<string, boolean>> {
    const response = await get<ApiResponse<Record<string, boolean>>>(
      `${getBaseUrl(orgId)}/modules/${moduleCode}/features`
    );
    return response.data;
  },

  /**
   * Update grace period days for an organization subscription.
   */
  async updateGracePeriod(orgId: string, days: number): Promise<number> {
    const response = await put<ApiResponse<number>>(
      `${getBaseUrl(orgId)}/grace-period?days=${days}`,
      {}
    );
    return response.data;
  },
};

export default subscriptionsService;
